import { Instagram, Music2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Picture } from "./Picture";
import {
  ADDRESS_LINE,
  EMAIL,
  INSTAGRAM_URL,
  LOGO,
  MAPS_URL,
  NAV_LINKS,
  PHONE_DISPLAY,
  PHONE_HREF,
  PLAZA_NAME,
  TIKTOK_URL,
} from "./data";

function homeHref(href: string) {
  return href.startsWith("#") ? `/${href}` : href;
}

export function Footer({ compact = false }: { compact?: boolean }) {
  return (
    <footer
      className={cn(
        "surface-charcoal grain pt-16 text-cream/75",
        compact ? "pb-16" : "pb-28 sm:pb-16",
      )}
    >
      <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
        <div>
          <Picture
            image={LOGO}
            alt="The Glam Room logo"
            sizes="64px"
            imgClassName="h-16 w-auto brightness-0 invert"
          />
          <p className="mt-3 font-display text-xl tracking-[0.22em] text-cream uppercase">
            The Glam Room
          </p>
          <p className="mt-4 text-sm leading-relaxed">
            Wellington's luxury hair salon and extension bar. Family owned, boho-chic, and always
            glad to see you.
          </p>
          <div className="mt-5 flex gap-3">
            {[
              { icon: Instagram, label: "Instagram", href: INSTAGRAM_URL },
              { icon: Music2, label: "TikTok", href: TIKTOK_URL },
            ].map(({ icon: Icon, label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`The Glam Room on ${label}`}
                className="grid h-11 w-11 place-items-center rounded-full border border-cream/25 text-cream transition-colors hover:bg-cream/10"
              >
                <Icon className="h-4 w-4" strokeWidth={1.4} aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-[0.72rem] tracking-[0.2em] text-gold uppercase">Visit</h2>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-cream"
              >
                {ADDRESS_LINE}
              </a>
              <span className="mt-1 block text-cream/55">{PLAZA_NAME}</span>
            </li>
            <li>
              <a href={PHONE_HREF} className="hover:text-cream">
                {PHONE_DISPLAY}
              </a>
            </li>
            <li>
              <a href={`mailto:${EMAIL}`} className="hover:text-cream">
                {EMAIL}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h2 className="text-[0.72rem] tracking-[0.2em] text-gold uppercase">Hours</h2>
          <ul className="mt-4 space-y-1.5 text-sm">
            <li>Tue &amp; Thu · 10am – 7pm</li>
            <li>Wed, Fri &amp; Sat · 9am – 5pm</li>
            <li>Sun &amp; Mon · Closed</li>
          </ul>
        </div>

        <div>
          <h2 className="text-[0.72rem] tracking-[0.2em] text-gold uppercase">Explore</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a href={homeHref(l.href)} className="hover:text-cream">
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mx-auto mt-12 flex max-w-7xl flex-col gap-2 border-t border-cream/15 px-5 pt-6 text-xs sm:flex-row sm:items-center sm:justify-between lg:px-8">
        <p>© {new Date().getFullYear()} The Glam Room. All rights reserved.</p>
        <Link to="/privacy" className="hover:text-cream">
          Privacy Policy
        </Link>
      </div>
    </footer>
  );
}
